import React, { createContext, FC, ReactNode, useContext, useMemo } from "react";
import invariant from "tiny-invariant";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Customer } from "../../../projection/customer/customer";
import { KameleoonEnvironment } from "../../ab-testing/kameleoonEnvironment";

interface StaticInfo {
  readonly kameleoon: KameleoonEnvironment;
  readonly customer: Customer;
  readonly tradename: Tradename;
  readonly basePath: string;
}

const StaticInfoContext = createContext<StaticInfo>(null as unknown as StaticInfo);

interface StaticInfoProviderProps {
  readonly children: ReactNode;
  readonly kameleoon: KameleoonEnvironment;
  readonly customer: Customer;
  readonly tradename: Tradename;
  readonly basePath: string;
}

const StaticInfoProvider: FC<StaticInfoProviderProps> = ({ children, kameleoon, customer, tradename, basePath }) => {
  const value = useMemo(
    () => ({
      kameleoon,
      customer,
      tradename,
      basePath,
    }),
    [basePath, customer, kameleoon, tradename],
  );

  return <StaticInfoContext.Provider value={value}>{children}</StaticInfoContext.Provider>;
};

const useStaticInfo = () => {
  const staticInfo = useContext(StaticInfoContext);

  invariant(
    staticInfo,
    "Your are trying to use the useStaticInfo hook without wrapping your app with the <StaticInfoProvider>.",
  );

  return staticInfo;
};

export { useStaticInfo, StaticInfoProvider };
